import { Calendar, CheckCircle, Phone, Building2, TreePine, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function MaintenanceContractsSection() {
  const contracts = [
    {
      duration: "6 Month",
      title: "Seasonal Care",
      description: "Perfect for properties needing focused seasonal attention during peak growth or monsoon periods.",
      features: [
        "Monthly tree inspections",
        "Scheduled trimming & pruning",
        "Fire safety brush clearing",
        "Storm damage priority response",
        "Dedicated account manager",
      ],
      highlight: false,
    },
    {
      duration: "12 Month",
      title: "Annual Excellence",
      description: "Comprehensive year-round care ensuring your property maintains pristine landscaping every season.",
      features: [
        "Bi-weekly property visits",
        "Full tree & shrub maintenance",
        "Fire safety compliance program",
        "24/7 priority emergency service",
        "Quarterly ISA Arborist assessments",
        "Dedicated landscape coordinator",
      ],
      highlight: true,
      savings: "Best Value",
    },
  ];

  const golfCourseServices = [
    "Fairway tree management",
    "Hazard tree identification & removal",
    "Aesthetic tree shaping for course views",
    "Tournament-ready preparation",
    "Irrigation zone tree care",
    "Cart path clearance maintenance",
  ];

  return (
    <section
      id="maintenance-contracts"
      className="py-28 lg:py-36 bg-gradient-to-br from-[#0A1628] via-[#1E3A5F] to-[#0A1628]"
      data-testid="maintenance-contracts-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">
            Commercial Property Solutions
          </p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Maintenance Contracts &
            <br />
            <span className="text-[#D4A853]">Golf Course Care</span>
          </h2>
          <p className="text-white/70 max-w-3xl mx-auto text-lg">
            Tailored landscaping management programs for HOAs, hotels, commercial properties, 
            and premier golf courses. Protect your investment with consistent, professional care.
          </p>
        </div>

        {/* Commercial Badge */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center gap-3 bg-[#D4A853]/20 border border-[#D4A853]/40 px-6 py-3">
            <Building2 className="w-5 h-5 text-[#D4A853]" />
            <span className="text-[#D4A853] font-semibold uppercase tracking-wider text-sm">
              Commercial Properties Only
            </span>
          </div>
        </div>

        {/* Contract Options */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {contracts.map((contract, index) => (
            <div
              key={index}
              className={`relative p-10 ${
                contract.highlight
                  ? "bg-gradient-to-br from-[#10B981]/20 to-[#10B981]/5 border-2 border-[#10B981]"
                  : "bg-white/5 border border-white/20"
              }`}
              data-testid={`contract-${contract.duration.toLowerCase().replace(" ", "-")}`}
            >
              {contract.savings && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#D4A853] text-[#0A1628] px-6 py-1.5 text-sm font-bold uppercase tracking-wider">
                  {contract.savings}
                </div>
              )}
              
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-6 h-6 text-[#10B981]" />
                <span className="text-[#10B981] font-bold uppercase tracking-wider">
                  {contract.duration} Contract
                </span>
              </div>
              
              <h3 className="font-['Cormorant_Garamond'] text-3xl font-bold text-white mb-4">
                {contract.title}
              </h3>
              
              <p className="text-white/70 mb-8">{contract.description}</p>
              
              <div className="space-y-4 mb-8">
                {contract.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                    <span className="text-white">{feature}</span>
                  </div>
                ))}
              </div>
              
              <Button
                asChild
                className={`w-full rounded-none py-5 uppercase tracking-[0.15em] text-sm font-bold ${
                  contract.highlight
                    ? "bg-gradient-to-r from-[#10B981] to-[#059669] text-white shadow-lg shadow-emerald-500/30"
                    : "bg-white/10 text-white hover:bg-white/20 border border-white/30"
                }`}
                data-testid={`contract-cta-${index}`}
              >
                <a href="#contact" className="flex items-center justify-center gap-2">
                  Request Custom Quote
                  <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
            </div>
          ))}
        </div>

        {/* Golf Course Care Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628] to-transparent z-10" />
          <img
            src="https://images.unsplash.com/photo-1758190153295-710199944b73?w=1200&q=80"
            alt="Beautiful Golf Course with Trees"
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          
          <div className="relative z-20 p-10 lg:p-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <TreePine className="w-8 h-8 text-[#10B981]" />
                  <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase">
                    Specialized Service
                  </p>
                </div>
                
                <h3 className="font-['Cormorant_Garamond'] text-3xl lg:text-4xl font-bold text-white mb-6">
                  Golf Course
                  <br />
                  <span className="text-[#D4A853]">Lawn & Tree Care</span>
                </h3>
                
                <p className="text-white/70 text-lg mb-8">
                  Scottsdale and Paradise Valley's premier golf courses trust Celebrity Tree Service 
                  for course-ready landscaping. We understand the unique demands of maintaining 
                  championship-level aesthetics while ensuring player safety.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {golfCourseServices.map((service, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <Sparkles className="w-4 h-4 text-[#D4A853] flex-shrink-0" />
                      <span className="text-white text-sm">{service}</span>
                    </div>
                  ))}
                </div>
                
                <Button
                  asChild
                  className="bg-gradient-to-r from-[#D4A853] to-[#B8923A] hover:from-[#B8923A] hover:to-[#9A7B2F] text-[#0A1628] rounded-none px-10 py-5 uppercase tracking-[0.15em] text-sm font-bold"
                  data-testid="golf-course-cta"
                >
                  <a href="#contact" className="flex items-center gap-2">
                    Request Golf Course Quote
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </Button>
              </div>
              
              <div className="hidden lg:block">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-8">
                  <h4 className="font-['Cormorant_Garamond'] text-2xl font-bold text-white mb-4">
                    Contract Options
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-white/10">
                      <span className="text-white">6 Month Seasonal</span>
                      <span className="text-[#10B981] font-semibold">Custom Quote</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-white/10">
                      <span className="text-white">12 Month Annual</span>
                      <span className="text-[#D4A853] font-semibold">Best Value</span>
                    </div>
                  </div>
                  <p className="text-white/50 text-sm mt-6">
                    All contracts include dedicated account management and priority scheduling.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Residential Notice */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-white/5 border border-white/20 px-8 py-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Phone className="w-5 h-5 text-[#10B981]" />
              <span className="text-white font-semibold">Residential Properties</span>
            </div>
            <p className="text-white/70 mb-4">
              Looking for residential tree care or maintenance?
            </p>
            <a
              href="tel:4803517775"
              className="text-[#10B981] font-bold text-xl hover:text-[#D4A853] transition-colors"
            >
              Call (480) 351-7775 to Request Service
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
